import type {
  MediaDescriptor,
  MediaRendition,
  RenditionDescriptor,
} from "@aasaan-share/protocol";

const DATABASE_NAME = "aasaan-share-viewer";
const DATABASE_VERSION = 1;
const STORE_NAME = "media";

export interface AuthorizedCachedShare {
  readonly expiresAt: string;
  readonly revision: string;
  readonly shareId: string;
}

export interface MediaCacheRequest extends AuthorizedCachedShare {
  readonly descriptor: MediaDescriptor;
  readonly rendition: MediaRendition;
}

export interface MediaCacheRecord {
  readonly blob: Blob;
  readonly byteLength: number;
  readonly expiresAt: number;
  readonly key: string;
  readonly mediaId: string;
  readonly mimeType: string;
  readonly rendition: MediaRendition;
  readonly revision: string;
  readonly sha256?: string;
  readonly shareId: string;
}

export interface MediaCacheStore {
  delete(key: string): Promise<void>;
  deleteWhere(predicate: (record: MediaCacheRecord) => boolean): Promise<void>;
  get(key: string): Promise<MediaCacheRecord | undefined>;
  put(record: MediaCacheRecord): Promise<void>;
}

export class ViewerMediaCache {
  constructor(
    private readonly store: MediaCacheStore = new IndexedDbMediaCacheStore(),
    private readonly now: () => number = Date.now,
  ) {}

  async get(request: MediaCacheRequest): Promise<Blob | null> {
    const expiresAt = Date.parse(request.expiresAt);
    const key = cacheKey(request);
    if (!Number.isFinite(expiresAt) || expiresAt <= this.now()) {
      await this.ignoreFailure(this.store.delete(key));
      return null;
    }

    try {
      const record = await this.store.get(key);
      if (!record) return null;
      const rendition = renditionDescriptor(request);
      if (!rendition || !matches(record, request, rendition)) {
        await this.ignoreFailure(this.store.delete(key));
        return null;
      }
      return record.blob;
    } catch {
      return null;
    }
  }

  async put(request: MediaCacheRequest, blob: Blob): Promise<void> {
    const expiresAt = Date.parse(request.expiresAt);
    const rendition = renditionDescriptor(request);
    if (
      !rendition
      || !Number.isFinite(expiresAt)
      || expiresAt <= this.now()
      || blob.size !== rendition.byteLength
      || blob.type !== rendition.mimeType
    ) {
      return;
    }

    await this.ignoreFailure(this.store.put({
      blob,
      byteLength: rendition.byteLength,
      expiresAt,
      key: cacheKey(request),
      mediaId: request.descriptor.mediaId,
      mimeType: rendition.mimeType,
      rendition: request.rendition,
      revision: request.revision,
      ...(rendition.sha256 ? { sha256: rendition.sha256 } : {}),
      shareId: request.shareId,
    }));
  }

  async reconcileAuthorizedShares(
    shares: readonly AuthorizedCachedShare[],
  ): Promise<void> {
    const now = this.now();
    const authorized = new Map(
      shares
        .map((share) => ({ ...share, expiresAtMs: Date.parse(share.expiresAt) }))
        .filter((share) => Number.isFinite(share.expiresAtMs) && share.expiresAtMs > now)
        .map((share) => [share.shareId, share]),
    );
    await this.ignoreFailure(this.store.deleteWhere((record) => {
      const share = authorized.get(record.shareId);
      return !share || share.revision !== record.revision;
    }));
  }

  async purgeExpired(): Promise<void> {
    const now = this.now();
    await this.ignoreFailure(this.store.deleteWhere((record) => (
      !Number.isFinite(record.expiresAt) || record.expiresAt <= now
    )));
  }

  async deleteShare(shareId: string): Promise<void> {
    await this.ignoreFailure(
      this.store.deleteWhere((record) => record.shareId === shareId),
    );
  }

  private async ignoreFailure(operation: Promise<unknown>): Promise<void> {
    try {
      await operation;
    } catch {
      // Persistent storage is an optimization; transfers remain the fallback.
    }
  }
}

class IndexedDbMediaCacheStore implements MediaCacheStore {
  #database: Promise<IDBDatabase> | null = null;

  async get(key: string): Promise<MediaCacheRecord | undefined> {
    const database = await this.database();
    const transaction = database.transaction(STORE_NAME, "readonly");
    const result = await requestResult<MediaCacheRecord | undefined>(
      transaction.objectStore(STORE_NAME).get(key),
    );
    await transactionComplete(transaction);
    return result;
  }

  async put(record: MediaCacheRecord): Promise<void> {
    const database = await this.database();
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).put(record);
    await transactionComplete(transaction);
  }

  async delete(key: string): Promise<void> {
    const database = await this.database();
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).delete(key);
    await transactionComplete(transaction);
  }

  async deleteWhere(
    predicate: (record: MediaCacheRecord) => boolean,
  ): Promise<void> {
    const database = await this.database();
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const request = transaction.objectStore(STORE_NAME).openCursor();
    request.addEventListener("success", () => {
      const cursor = request.result;
      if (!cursor) return;
      try {
        if (predicate(cursor.value as MediaCacheRecord)) cursor.delete();
        cursor.continue();
      } catch {
        transaction.abort();
      }
    });
    await transactionComplete(transaction);
  }

  private database(): Promise<IDBDatabase> {
    if (this.#database) return this.#database;
    this.#database = new Promise((resolve, reject) => {
      if (!globalThis.indexedDB) {
        reject(new Error("IndexedDB is unavailable"));
        return;
      }
      const request = globalThis.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      request.addEventListener("upgradeneeded", () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME, { keyPath: "key" });
        }
      });
      request.addEventListener("success", () => resolve(request.result));
      request.addEventListener("error", () => reject(request.error));
      request.addEventListener("blocked", () => {
        reject(new Error("IndexedDB upgrade was blocked"));
      });
    });
    return this.#database;
  }
}

function cacheKey(request: MediaCacheRequest): string {
  return JSON.stringify([
    request.shareId,
    request.revision,
    request.descriptor.mediaId,
    request.rendition,
  ]);
}

function renditionDescriptor(
  request: MediaCacheRequest,
): RenditionDescriptor | undefined {
  if (request.rendition === "thumbnail") {
    return request.descriptor.renditions.thumbnail;
  }
  if (request.rendition === "original") {
    return request.descriptor.renditions.original;
  }
  return request.descriptor.kind === "image"
    ? request.descriptor.renditions.preview
    : undefined;
}

function matches(
  record: MediaCacheRecord,
  request: MediaCacheRequest,
  rendition: RenditionDescriptor,
): boolean {
  return record.key === cacheKey(request)
    && record.shareId === request.shareId
    && record.revision === request.revision
    && record.mediaId === request.descriptor.mediaId
    && record.rendition === request.rendition
    && record.byteLength === rendition.byteLength
    && record.mimeType === rendition.mimeType
    && record.sha256 === rendition.sha256
    && record.blob.size === rendition.byteLength
    && record.blob.type === rendition.mimeType;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result));
    request.addEventListener("error", () => reject(request.error));
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve());
    transaction.addEventListener("abort", () => reject(transaction.error));
    transaction.addEventListener("error", () => reject(transaction.error));
  });
}
